import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Row } from '@vendasta/galaxy/table';
import { firstValueFrom, map, Observable, take, forkJoin, catchError, of } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DialogData, SelectCampaignModalComponent } from './select-campaign-modal/select-campaign-modal.component';
import { YeswareCampaignsApiService } from '@vendasta/vendasta-yesware-bridge';
import { CampaignInterface } from '@vendasta/vendasta-yesware-bridge/lib/_internal/interfaces/campaigns.interface';
import { PagedRequestOptionsInterface } from '@vendasta/vendasta-yesware-bridge/lib/_internal/interfaces/paging.interface';
import { ListUsersCampaignsResponse } from '@vendasta/vendasta-yesware-bridge/lib/_internal/objects';
import { NamespaceInterface } from './campaign-actions.interfaces';

@Injectable({ providedIn: 'root' })
export class CampaignActionsService {
  private readonly dialog = inject(MatDialog);
  private readonly campaignsApiService = inject(YeswareCampaignsApiService);
  private readonly snackbarService = inject(SnackbarService);

  listCampaigns(
    namespace: NamespaceInterface,
    pagingOptions: PagedRequestOptionsInterface,
  ): Observable<ListUsersCampaignsResponse> {
    return this.campaignsApiService
      .listUsersCampaigns({
        namespace: namespace,
        pagingOptions: pagingOptions,
      })
      .pipe(take(1));
  }

  private async openSelectCampaignModal(
    namespace: NamespaceInterface,
    numberOfRows: number,
  ): Promise<CampaignInterface> {
    const data = {
      namespace: namespace,
      numberOfRows: numberOfRows,
    } as DialogData;
    const dialog = this.dialog.open(SelectCampaignModalComponent, {
      data: data,
      width: '500px',
    });
    return firstValueFrom(dialog.afterClosed());
  }

  async addToCampaign(namespace: NamespaceInterface, rows: Row[]): Promise<void> {
    const resourceIds = rows.map((row) => row.id).filter((id) => id);
    const selectedList = await this.openSelectCampaignModal(namespace, resourceIds.length);
    if (!selectedList || resourceIds.length === 0) {
      return;
    }

    // Create an array of observables, one for each contact to add
    const addRequests = resourceIds.map((contactId) =>
      this.campaignsApiService
        .addContactToCampaign({
          namespace: {
            partnerId: namespace.partnerId,
            accountGroupId: namespace.accountGroupId,
          },
          campaignId: selectedList.campaignId,
          contactId: contactId,
        })
        .pipe(
          take(1),
          // Map successful responses to true
          map(() => true),
          // Catch errors at the individual request level
          catchError((error) => {
            console.error(`Error adding contact ${contactId} to campaign:`, error);
            // Return false to indicate this specific request failed
            return of(false);
          }),
        ),
    );

    // Use forkJoin to execute all requests in parallel
    forkJoin(addRequests).subscribe({
      next: (results) => {
        const successCount = results.filter((result) => result).length;

        if (successCount === resourceIds.length) {
          // All succeeded
          const messageKey =
            resourceIds.length === 1
              ? 'YESWARE.SUCCESS.ADDED_TO_CAMPAIGN_SINGULAR'
              : 'YESWARE.SUCCESS.ADDED_TO_CAMPAIGN';
          this.snackbarService.openSuccessSnack(messageKey, {
            interpolateTranslateParams: {
              numberOfContacts: resourceIds.length?.toString() ?? '0',
              name: selectedList.name || '',
            },
          });
        } else if (successCount === 0) {
          // All failed
          this.snackbarService.openErrorSnack('YESWARE.ERRORS.FAILED_TO_ADD', {
            interpolateTranslateParams: {
              name: selectedList?.name || '',
            },
          });
        } else {
          // Some failed
          this.snackbarService.openSuccessSnack('YESWARE.SUCCESS.ADDED_TO_CAMPAIGN_PARTIAL', {
            interpolateTranslateParams: {
              successCount: successCount.toString(),
              failedCount: (resourceIds.length - successCount).toString(),
              name: selectedList.name || '',
            },
          });
        }
      },
      error: () => {
        this.snackbarService.openErrorSnack('YESWARE.ERRORS.FAILED_TO_ADD', {
          interpolateTranslateParams: {
            name: selectedList?.name || '',
          },
        });
      },
    });
  }
}
